import { Component, OnInit, OnDestroy } from '@angular/core';
import { ServerTokenService } from '../../services/server-token.service';
import { CustomAuthServiceService } from '../../auth/custom-auth-service.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface ApiKeyRow {
  _id: string;
  name: string;
  hint: string;
  regionId: number;
  facilities: number[];
  active: boolean;
  createdAt: string;
}

interface Facility {
  fac_id: number;
  name: string;
}

@Component({
  selector: 'admin-api-keys',
  templateUrl: './api-keys.component.html',
  styleUrls: ['./api-keys.component.scss'],
})
export class ApiKeysComponent implements OnInit, OnDestroy {
  keys: ApiKeyRow[] = [];
  facilities: Facility[] = [];
  selectedFacilities = new Set<number>();
  createdKey: string | null = null;
  loading = false;
  regionId: number;

  name = '';
  private destroy$ = new Subject<void>();

  constructor(
    private server: ServerTokenService,
    private authService: CustomAuthServiceService,
  ) {}

  ngOnInit() {
    this.regionId = this.authService.currentUserSubject?.settings?.regionId;
    this.loadKeys();
    this.loadFacilities();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadKeys() {
    this.server.getWithToken<{ data: ApiKeyRow[] }>('snfs/admin/api-keys').pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => this.keys = res.data || [],
    });
  }

  loadFacilities() {
    if (!this.regionId) return;
    this.server.getWithToken<{ data: Facility[] }>(`snfs/admin/api-keys/facilities?regionId=${this.regionId}`).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => this.facilities = res.data || [],
    });
  }

  toggleFacility(facId: number) {
    if (this.selectedFacilities.has(facId)) {
      this.selectedFacilities.delete(facId);
    } else {
      this.selectedFacilities.add(facId);
    }
  }

  selectAll() {
    this.facilities.forEach(f => this.selectedFacilities.add(f.fac_id));
  }

  deselectAll() {
    this.selectedFacilities.clear();
  }

  get canCreate(): boolean {
    return !!this.name.trim() && this.selectedFacilities.size > 0;
  }

  create() {
    if (!this.canCreate) return;
    this.loading = true;
    this.createdKey = null;
    const body = {
      name: this.name.trim(),
      facilities: Array.from(this.selectedFacilities),
    };
    this.server.postWithToken('snfs/admin/api-keys', body).pipe(takeUntil(this.destroy$)).subscribe({
      next: (res: any) => {
        this.createdKey = res.key;
        this.loading = false;
        this.loadKeys();
        this.name = '';
        this.selectedFacilities.clear();
      },
      error: () => this.loading = false,
    });
  }

  revoke(key: ApiKeyRow) {
    if (!confirm(`Revoke API key "${key.name}"?`)) return;
    this.server.deleteWithToken(`snfs/admin/api-keys/${key._id}`).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.loadKeys(),
    });
  }

  copyKey() {
    if (this.createdKey) {
      navigator.clipboard.writeText(this.createdKey);
    }
  }
}
